import { useEffect, useState } from "react";
import { Paper } from "@mui/material";
import { Geocoder } from "@mapbox/search-js-react";
import type { GeocodingFeature } from "@mapbox/search-js-core";
import { Marker } from "mapbox-gl";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { useMap } from "react-map-gl/mapbox";

import { FIT_BOUNDS_OPTIONS } from "components/PlotMap/utils/mapCameraUtils";

import {
  parseCoordinateQuery,
  SEARCH_RESULT_ZOOM,
  toSearchResultView,
  type SearchResultView,
} from "./searchResultView";

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface MapSearchProps {}

/** Place / address / coordinate search that moves the map to the picked result. */
const MapSearch: React.FC<MapSearchProps> = () => {
  const { default: map } = useMap();
  const [prefs] = usePagePrefs();
  const [searchText, setSearchText] = useState("");
  const [pin, setPin] = useState<[number, number] | null>(null);
  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const mapboxMap = map?.getMap();
  const isHidden = locationUuid !== "";

  useEffect(() => {
    if (pin == null || mapboxMap == null || isHidden) {
      return undefined;
    }

    const marker = new Marker().setLngLat(pin).addTo(mapboxMap);
    return () => {
      marker.remove();
    };
  }, [isHidden, mapboxMap, pin]);

  const moveToView = (view: SearchResultView | null) => {
    if (view == null || map == null) {
      return;
    }

    if (view.bounds != null) {
      map.fitBounds(view.bounds, { ...FIT_BOUNDS_OPTIONS, duration: 1000 });
      return;
    }

    map.flyTo({
      center: view.center,
      zoom: view.zoom ?? SEARCH_RESULT_ZOOM,
      duration: 1000,
    });
  };

  const handleChange = (nextSearchText: string) => {
    setSearchText(nextSearchText);
    if (nextSearchText === "") {
      setPin(null);
    }
  };

  const handleClear = () => {
    setSearchText("");
    setPin(null);
  };

  const handleRetrieve = (feature: GeocodingFeature) => {
    setPin(null);
    moveToView(toSearchResultView(feature));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }
    const view = parseCoordinateQuery(searchText);
    if (view != null) {
      setPin(view.center);
      moveToView(view);
    }
  };

  if (isHidden) {
    return null;
  }

  return (
    <Paper
      aria-label="Map search"
      elevation={3}
      onKeyDown={handleKeyDown}
      role="search"
      sx={{ width: 320 }}
    >
      <Geocoder
        accessToken={MAPBOX_ACCESS_TOKEN}
        componentOptions={{ flipCoordinates: true }}
        onChange={handleChange}
        onClear={handleClear}
        onRetrieve={handleRetrieve}
        placeholder="Search town or lat, lng"
        value={searchText}
      />
    </Paper>
  );
};

export default MapSearch;
